import type { CustomDiceRollerProps } from "@/components/CustomDiceRoller";
import { D100 } from "@/components/CustomDiceRoller/components/D100";
import {
  getMediaUrl,
  initialDieTypeQuantities,
  PddPayloadTypes,
  RollTos,
} from "@/constants";
import { Context } from "@/context";
import { rollDice } from "@/helpers/rollDice";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import { useShared3dDiceSettingStore } from "@/hooks/useShared3dDiceSettingStore";
import type { DieTypeQuantities, RollTo } from "@/types";
import ArrowUpRightFromSquare from "@dndbeyond/fontawesome-cache/svgs/light/arrow-up-right-from-square.svg";
import D10 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d10.svg";
import D12 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d12.svg";
import D20 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d20.svg";
import D4 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d4.svg";
import D6 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d6.svg";
import D8 from "@dndbeyond/fontawesome-cache/svgs/light/dice-d8.svg";
import Plus from "@dndbeyond/fontawesome-cache/svgs/solid/plus.svg";
import User from "@dndbeyond/fontawesome-cache/svgs/solid/user.svg";
import Users from "@dndbeyond/fontawesome-cache/svgs/solid/users.svg";
import { Button } from "@dndbeyond/ttui/components/Button";
import { DismissableSection } from "@dndbeyond/ttui/components/DismissableSection";
import { ToggleSwitch } from "@dndbeyond/ttui/components/ToggleSwitch";
import { Tooltip } from "@dndbeyond/ttui/components/Tooltip";
import clsx from "clsx";
import {
  Fragment,
  useContext,
  useEffect,
  useState,
  type FC,
  type MouseEvent,
  type MouseEventHandler,
} from "react";
import styles from "./styles.module.css";

const Icons: { [key: string]: FC } = {
  d4: D4,
  d6: D6,
  d8: D8,
  d10: D10,
  d12: D12,
  d20: D20,
  d100: D100,
};
const DieIcon: FC<{
  notation: string;
  quantity: number;
  increment: MouseEventHandler<HTMLButtonElement>;
  decrement: MouseEventHandler<HTMLButtonElement>;
}> = ({ notation, quantity, increment, decrement }) => {
  const Icon = Icons[notation];

  return (
    <>
      <Button
        className={clsx([styles.die, styles[notation]])}
        data-quantity={quantity > 0 ? quantity : undefined}
        onClick={increment}
        onContextMenu={decrement}
        id={notation}
        data-testid={notation}
        data-tooltip-id={notation}
      >
        <Icon />
        <Plus className={styles.plus} />
        <span>{notation}</span>
      </Button>
      <Tooltip id={notation}>{quantity === 0 && `Add ${notation}`}</Tooltip>
    </>
  );
};

export const Roller: FC<CustomDiceRollerProps> = ({
  onClose,
  rollInstrumentationCallback,
  rollCallback,
  rollErrorCallback,
  canUseShared3dDice = false,
  setId,
  clearDiceCallback,
  rollToCallback,
  diceSets,
  gameId,
  preview,
  customDiceSet,
  entityType = "user",
  entityId,
  name,
  dmId,
  ...rest
}) => {
  const { nodeEnv, env, user, renderedWorker, hasPddErrored, isPddReady } =
    useContext(Context);
  const [diceToRoll, setDiceToRoll] = useState<DieTypeQuantities>(
    initialDieTypeQuantities,
  );
  const [isOnline, setIsOnline] = useState(globalThis.navigator?.onLine);
  const { setIsShared3dDiceSettingEnabled } = useShared3dDiceSettingStore();
  const isShared3dDiceEnabled = useShared3dDiceSettingStore((state) =>
    state.getIsShared3dDiceSettingEnabled(canUseShared3dDice, Number(user?.id)),
  );
  const { setRollToPrivacy } = useDiceRollPrivacyStore();
  const rollTo = useDiceRollPrivacyStore(
    (state) => state.getDiceRollPrivacy(Number(user?.id))?.rollTo,
  );
  const incrementDie = (e: MouseEvent<HTMLButtonElement>) =>
    setDiceToRoll({
      ...diceToRoll,
      [e.currentTarget.id]:
        diceToRoll[e.currentTarget.id as keyof DieTypeQuantities] + 1,
    });
  const decrementDie = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const die = e.currentTarget.id as keyof DieTypeQuantities;

    if (diceToRoll[die] === 0) return;

    setDiceToRoll({
      ...diceToRoll,
      [e.currentTarget.id]: Math.max(0, diceToRoll[die] - 1),
    });
  };
  const emptyDiceToRoll = Object.values(diceToRoll).every((i) => i === 0);
  const handleRoll = () => {
    setTimeout(() => {
      try {
        const payload = rollDice(
          {
            diceNotation: Object.entries(diceToRoll)
              .filter(([, quantity]) => quantity > 0)
              .map(([d, quantity]) => `${quantity}${d}`)
              .join("+"),
            action: "custom",
            rollType: "roll",
            entityId: entityId || String(user?.id),
            entityType,
            ...(user && { userId: String(user.id) }),
            setId: setId!,
            ...(gameId && { gameId: String(gameId) }),
            isShared3dDiceEnabled,
            name,
            dmId,
          },
          hasPddErrored,
          isPddReady,
          (instrumentationPayload) =>
            rollInstrumentationCallback?.(instrumentationPayload),
        );

        rollCallback?.(payload);
      } catch (exception) {
        rollErrorCallback?.(exception);
      }

      setDiceToRoll(initialDieTypeQuantities);
    });
  };
  const handleRollTo = (e: MouseEvent<HTMLButtonElement>) => {
    if (rollTo !== e.currentTarget.id) {
      setRollToPrivacy(Number(user?.id));
    }

    rollToCallback?.(e);
  };
  const isToggleEnabled = !hasPddErrored && isShared3dDiceEnabled && isPddReady;
  const getRollToText = (rollToOption: RollTo) =>
    rollToOption === RollTos.Self
      ? dmId === String(user?.id)
        ? "self"
        : "DM"
      : "everyone";

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    globalThis.addEventListener("online", handleOnline);
    globalThis.addEventListener("offline", handleOffline);

    return () => {
      globalThis.removeEventListener("online", handleOnline);
      globalThis.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <DismissableSection heading="Roll Dice" onClose={onClose} {...rest}>
      <div className={styles.wrapper}>
        {!preview && (
          <div className={styles.shared3dDiceContainer}>
            <div className={styles.shared3dDiceImageWrapper}>
              {!customDiceSet && (
                <>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    className={clsx([
                      styles.shared3dDiceImage,
                      !isToggleEnabled && styles.shared3dDiceImageDisabled,
                    ])}
                    src={`${getMediaUrl(nodeEnv!, env!)}/dice/images/thumbnails/${setId}-d20-20.png`}
                    alt="Current Dice Set"
                    width={128}
                    height={128}
                  />
                </>
              )}
            </div>
            <div className={styles.diceTextContainer}>
              <p className={styles.diceSetLabel}>
                {isToggleEnabled
                  ? customDiceSet
                    ? "Custom Dice Set"
                    : diceSets?.find(({ id }) => id === setId)?.name
                  : "3D Dice Disabled"}
              </p>
              {!preview && isOnline && (
                <a
                  href={`https://${env === "production" ? "www" : "stg"}.dndbeyond.com/my-dice`}
                  className={styles.changeDiceSetLink}
                  target="_blank"
                >
                  Change Dice <ArrowUpRightFromSquare />
                </a>
              )}
            </div>
            <div className={styles.toggleSwitchContainer}>
              <ToggleSwitch
                id="shared3dDiceToggleSwitch"
                isOn={isToggleEnabled}
                label=""
                onToggle={() => {
                  if (isShared3dDiceEnabled) {
                    renderedWorker?.postMessage({
                      type: PddPayloadTypes.ResetStore,
                    });
                  }

                  if (!hasPddErrored && isPddReady) {
                    setIsShared3dDiceSettingEnabled(Number(user?.id));
                  }
                }}
              />
            </div>
          </div>
        )}
        <div className={styles.diceContainer}>
          <div className={styles.dice}>
            {Object.keys(initialDieTypeQuantities).map((d) => (
              <DieIcon
                key={d}
                notation={d}
                quantity={diceToRoll[d as keyof DieTypeQuantities]}
                increment={incrementDie}
                decrement={decrementDie}
              />
            ))}
          </div>
          <div className={styles.buttonsWrapper}>
            <Button
              className={styles.clearButton}
              data-dd-action-name="Roll Dice Popup > Clear Dice"
              onClick={() => {
                setDiceToRoll(initialDieTypeQuantities);
                clearDiceCallback?.();
              }}
              data-testid="diceClearButton"
              size="smaller"
              disabled={emptyDiceToRoll}
            >
              Reset
            </Button>
            <Button
              className={styles.rollButton}
              data-dd-action-name="Roll Dice Popup > Roll Dice"
              onClick={handleRoll}
              data-testid="diceRollButton"
              size="smaller"
              disabled={emptyDiceToRoll}
              color="secondary"
            >
              Roll
            </Button>
          </div>
          {!preview && dmId && (
            <div className={styles.rollToWrapper}>
              <div className={styles.rollToContainer}>
                {Object.values(RollTos).map((rollToOption) => (
                  <Fragment key={rollToOption}>
                    <Button
                      id={rollToOption}
                      size="smaller"
                      className={clsx([
                        styles.rollToButton,
                        rollTo === rollToOption && styles.rollToActive,
                      ])}
                      data-dd-action-name={`Roll Dice Popup > Roll to ${rollToOption}`}
                      onClick={handleRollTo}
                      data-testid={`diceRollTo${rollToOption}Button`}
                      data-tooltip-id={`rollTo${rollToOption}`}
                    >
                      {rollToOption === RollTos.Self ? <User /> : <Users />}
                    </Button>
                    <Tooltip id={`rollTo${rollToOption}`}>
                      Roll to {getRollToText(rollToOption)}
                    </Tooltip>
                  </Fragment>
                ))}
              </div>
              <span className={styles.rollToLabel}>
                Rolling to {getRollToText(rollTo!)}
              </span>
            </div>
          )}
        </div>
      </div>
    </DismissableSection>
  );
};
